<template>
  <div class="catalog-page">
    <!-- Page Header with Search -->
    <header class="catalog-header">
      <div class="catalog-header__bg"></div>
      <div class="catalog-header__container">
        <nav class="catalog-header__breadcrumb" aria-label="Breadcrumb">
          <NuxtLink to="/" class="catalog-header__breadcrumb-link">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
            </svg>
            {{ t('nav.home') || 'Home' }}
          </NuxtLink>
          <span class="catalog-header__breadcrumb-sep">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </span>
          <span class="catalog-header__breadcrumb-current">{{ t('machines.title') || 'Stock List' }}</span>
        </nav>
        
        <div class="catalog-header__row">
          <div class="catalog-header__left">
            <h1 class="catalog-header__title">{{ t('machines.pageTitle') || 'Stock List' }}</h1>
            <p class="catalog-header__subtitle">{{ t('machines.pageDescription') || 'Browse our inventory of quality printing equipment' }}</p>
            <div class="catalog-header__stats">
              <div class="catalog-header__stat">
                <span class="catalog-header__stat-number">{{ machinesCount }}</span>
                <span class="catalog-header__stat-label">{{ t('machines.results.total') || 'Machines' }}</span>
              </div>
            </div>
          </div>
          
          <!-- Search Bar - Right Side -->
          <div class="catalog-header__search">
            <div class="catalog-search">
              <svg class="catalog-search__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="t('machines.search.placeholder') || 'Search machines...'"
                class="catalog-search__input"
                @input="debouncedSearch"
              />
              <button
                v-if="searchQuery"
                @click="clearSearch"
                class="catalog-search__clear"
                :aria-label="t('machines.search.clear') || 'Clear search'"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Filters Section -->
    <section class="catalog-filters">
      <div class="catalog-filters__container">
        <!-- Desktop: All in one row -->
        <div class="catalog-filters__row">
          <!-- Filter Pills -->
          <div class="catalog-filters__grid">
            <!-- Brand Filter -->
            <div class="catalog-filter">
              <select v-model="filters.brand_id" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.allBrands') || 'All Brands' }}</option>
                <option v-for="brand in availableFilters.brands" :key="brand.id" :value="brand.id">{{ brand.name }}</option>
              </select>
            </div>

            <!-- Category Filter -->
            <div class="catalog-filter">
              <select v-model="filters.category_id" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.allCategories') || 'All Categories' }}</option>
                <option v-for="category in availableFilters.categories" :key="category.id" :value="category.id">{{ category.name }}</option>
              </select>
            </div>

            <!-- Condition Filter -->
            <div class="catalog-filter">
              <select v-model="filters.condition" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.anyCondition') || 'Condition' }}</option>
                <option value="new">{{ t('machines.status.new') || 'New' }}</option>
                <option value="used">{{ t('machines.status.used') || 'Used' }}</option>
                <option value="refurbished">{{ t('machines.status.refurbished') || 'Refurbished' }}</option>
              </select>
            </div>

            <!-- Colors Filter -->
            <div class="catalog-filter">
              <select v-model="filters.colors" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.anyColors') || 'Colors' }}</option>
                <option v-for="(label, value) in availableFilters.printing_colors" :key="value" :value="value">{{ label }}</option>
              </select>
            </div>

            <!-- Year Range -->
            <div class="catalog-filter catalog-filter--year">
              <div class="catalog-filter__year-range">
                <input
                  v-model="filters.min_year"
                  type="number"
                  :placeholder="t('machines.filters.from') || 'From'"
                  class="catalog-filter__year-input"
                  min="1980"
                  :max="currentYear"
                  @change="searchMachines"
                />
                <span class="catalog-filter__year-separator">–</span>
                <input
                  v-model="filters.max_year"
                  type="number"
                  :placeholder="t('machines.filters.to') || 'To'"
                  class="catalog-filter__year-input"
                  min="1980"
                  :max="currentYear"
                  @change="searchMachines"
                />
              </div>
            </div>
          </div>

          <!-- Actions: Clear + Sort -->
          <div class="catalog-filters__actions">
            <button
              v-if="activeFiltersCount > 0"
              @click="clearFilters"
              class="catalog-btn catalog-btn--danger"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
              {{ t('machines.filters.clear') || 'Clear' }}
            </button>
            
            <div class="catalog-sort-dropdown" :class="{ 'is-open': sortDropdownOpen }">
              <button @click.stop="sortDropdownOpen = !sortDropdownOpen" class="catalog-sort-dropdown__trigger">
                <svg class="catalog-sort-dropdown__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/>
                </svg>
                <span class="catalog-sort-dropdown__label">{{ currentSortLabel }}</span>
                <svg class="catalog-sort-dropdown__chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <div class="catalog-sort-dropdown__menu" v-show="sortDropdownOpen" @click.stop>
                <button 
                  v-for="option in machinesSortOptions" 
                  :key="option.value"
                  @click="selectSort(option.value)"
                  class="catalog-sort-dropdown__item"
                  :class="{ 'is-active': sortBy === option.value }"
                >
                  <svg class="catalog-sort-dropdown__item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="option.icon"/>
                  </svg>
                  <span class="catalog-sort-dropdown__item-label">{{ option.label }}</span>
                  <svg v-if="sortBy === option.value" class="catalog-sort-dropdown__check" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Filter Toggle -->
        <button
          @click="toggleFilters"
          class="catalog-filters__toggle"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
          </svg>
          <span>{{ t('machines.filters.title') || 'Filters' }}</span>
          <span v-if="activeFiltersCount > 0" class="catalog-filters__badge">{{ activeFiltersCount }}</span>
        </button>

        <!-- Mobile Filter Panel -->
        <div 
          class="catalog-filters__controls"
          :class="{ 'catalog-filters__controls--visible': filtersVisible }"
        >
          <div class="catalog-filters__grid">
            <div class="catalog-filter">
              <select v-model="filters.brand_id" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.allBrands') || 'All Brands' }}</option>
                <option v-for="brand in availableFilters.brands" :key="brand.id" :value="brand.id">{{ brand.name }}</option>
              </select>
            </div>
            <div class="catalog-filter">
              <select v-model="filters.category_id" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.allCategories') || 'All Categories' }}</option>
                <option v-for="category in availableFilters.categories" :key="category.id" :value="category.id">{{ category.name }}</option>
              </select>
            </div>
            <div class="catalog-filter">
              <select v-model="filters.condition" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.anyCondition') || 'Condition' }}</option>
                <option value="new">{{ t('machines.status.new') || 'New' }}</option>
                <option value="used">{{ t('machines.status.used') || 'Used' }}</option>
                <option value="refurbished">{{ t('machines.status.refurbished') || 'Refurbished' }}</option>
              </select>
            </div>
            <div class="catalog-filter">
              <select v-model="filters.colors" @change="searchMachines" class="catalog-filter__select">
                <option value="">{{ t('machines.filters.anyColors') || 'Colors' }}</option>
                <option v-for="(label, value) in availableFilters.printing_colors" :key="value" :value="value">{{ label }}</option>
              </select>
            </div>
            <div class="catalog-filter catalog-filter--year">
              <div class="catalog-filter__year-range">
                <input
                  v-model="filters.min_year"
                  type="number"
                  :placeholder="t('machines.filters.from') || 'From'"
                  class="catalog-filter__year-input"
                  min="1980"
                  :max="currentYear"
                  @change="searchMachines"
                />
                <span class="catalog-filter__year-separator">–</span>
                <input
                  v-model="filters.max_year"
                  type="number"
                  :placeholder="t('machines.filters.to') || 'To'"
                  class="catalog-filter__year-input"
                  min="1980"
                  :max="currentYear"
                  @change="searchMachines"
                />
              </div>
            </div>
          </div>
          <div class="catalog-filters__actions">
            <button
              v-if="activeFiltersCount > 0"
              @click="clearFilters"
              class="catalog-btn catalog-btn--danger"
            >
              {{ t('machines.filters.clearAll') || 'Clear Filters' }}
            </button>
            <div class="catalog-sort-dropdown" :class="{ 'is-open': sortDropdownOpen }">
              <button @click.stop="sortDropdownOpen = !sortDropdownOpen" class="catalog-sort-dropdown__trigger">
                <svg class="catalog-sort-dropdown__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/>
                </svg>
                <span class="catalog-sort-dropdown__label">{{ currentSortLabel }}</span>
                <svg class="catalog-sort-dropdown__chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <div class="catalog-sort-dropdown__menu" v-show="sortDropdownOpen" @click.stop>
                <button 
                  v-for="option in machinesSortOptions" 
                  :key="option.value"
                  @click="selectSort(option.value)"
                  class="catalog-sort-dropdown__item"
                  :class="{ 'is-active': sortBy === option.value }"
                >
                  <svg class="catalog-sort-dropdown__item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="option.icon"/>
                  </svg>
                  <span class="catalog-sort-dropdown__item-label">{{ option.label }}</span>
                  <svg v-if="sortBy === option.value" class="catalog-sort-dropdown__check" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Results Info Bar -->
    <div class="catalog-results-bar">
      <div class="catalog-results-bar__container">
        <p class="catalog-results-bar__info">
          <span class="catalog-results-bar__count">{{ machinesCount }}</span>
          {{ machinesCount === 1 ? t('machines.results.machineFound') : t('machines.results.machinesFoundPlural') }}
          <span v-if="pagination" class="catalog-results-bar__pagination">
            · {{ t('machines.results.page') || 'Page' }} {{ pagination.current_page }} {{ t('machines.results.of') || 'of' }} {{ pagination.last_page }}
          </span>
        </p>
        <div class="catalog-results-bar__per-page">
          <span class="catalog-results-bar__per-page-label">{{ t('machines.results.show') || 'Show' }}:</span>
          <div class="catalog-results-bar__per-page-buttons">
            <button
              v-for="option in [12, 24, 48]"
              :key="option"
              @click="handlePerPageChange(option)"
              :class="[
                'catalog-results-bar__per-page-btn',
                { 'catalog-results-bar__per-page-btn--active': perPage === option }
              ]"
            >
              {{ option }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <main class="catalog-main">
      <div class="catalog-main__container">
        <!-- Loading State -->
        <div v-if="loading" class="catalog-loading">
          <div v-for="i in 4" :key="i" class="catalog-skeleton">
            <div class="catalog-skeleton__image"></div>
            <div class="catalog-skeleton__content">
              <div class="catalog-skeleton__info">
                <div class="catalog-skeleton__title"></div>
                <div class="catalog-skeleton__subtitle"></div>
                <div class="catalog-skeleton__specs">
                  <div class="catalog-skeleton__spec"></div>
                  <div class="catalog-skeleton__spec"></div>
                  <div class="catalog-skeleton__spec"></div>
                  <div class="catalog-skeleton__spec"></div>
                </div>
              </div>
              <div class="catalog-skeleton__status"></div>
              <div class="catalog-skeleton__actions">
                <div class="catalog-skeleton__btn"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="machinesCount === 0" class="catalog-empty">
          <div class="catalog-empty__icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <h3 class="catalog-empty__title">{{ t('machines.noResults.title') || 'No machines found' }}</h3>
          <p class="catalog-empty__text">{{ t('machines.noResults.description') || 'Try adjusting your filters or search criteria' }}</p>
          <button @click="clearFilters" class="catalog-btn catalog-btn--primary">
            {{ t('machines.filters.clearAll') || 'Clear All Filters' }}
          </button>
        </div>

        <!-- Results Grid -->
        <div v-else class="catalog-grid">
          <MachineListCard
            v-for="machine in paginatedMachines"
            :key="machine.id"
            :machine="machine"
            @inquire="showMachineInquiryModal($event)"
          />
        </div>

        <!-- Pagination -->
        <div v-if="machinesCount > 0" class="catalog-pagination">
          <Pagination
            v-model:current-page="currentPage"
            :total-items="machinesCount"
            :items-per-page="perPage"
            :pagination="pagination"
            @update:current-page="handlePageChange"
          />
        </div>
      </div>
    </main>

    <!-- Inquiry Modal -->
    <MachineInquiryModal
      v-if="showInquiryModal"
      :machine="selectedMachineForInquiry"
      @close="showInquiryModal = false"
    />
  </div>
</template>

<script setup>
import { useDebounceFn } from "@vueuse/core";
import Pagination from "~/components/ui/Pagination.vue";
import MachineListCard from "~/components/machines/MachineListCard.vue";
import MachineInquiryModal from "~/components/machines/MachineInquiryModal.vue";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const showInquiryModal = ref(false);
const selectedMachineForInquiry = ref(null);

const showMachineInquiryModal = (machine) => {
  selectedMachineForInquiry.value = machine;
  showInquiryModal.value = true;
};

useHead({
  title: computed(() => t('meta.machinesTitle') || 'Used Press Machines - Technomac SNC'),
  meta: [
    {
      name: "description",
      content: computed(() => t('meta.machinesDescription') || 'Browse our inventory of quality used printing equipment from top brands.'),
    },
  ],
});

const { $api } = useNuxtApp();
const machines = ref([]);
const loading = ref(true);
const filtersVisible = ref(false);

const searchQuery = ref("");
const filters = ref({
  brand_id: "",
  category_id: "",
  colors: "",
  condition: "",
  location: "",
  min_year: "",
  max_year: "",
  status: "",  // Empty to let backend handle default (published + sold)
});
const sortBy = ref("created_at");
const sortDropdownOpen = ref(false);
const availableFilters = ref({
  brands: [],
  categories: [],
  printing_colors: {},
  locations: [],
});
const pagination = ref(null);
const currentPage = ref(1);
const totalMachines = ref(0);
const perPage = ref(12);

const activeFiltersCount = computed(() => {
  return Object.values(filters.value).filter(
    (value) => value !== "" && value !== null
  ).length;
});

const machinesCount = computed(() => {
  if (pagination.value && pagination.value.total !== undefined) {
    return pagination.value.total;
  }
  return totalMachines.value || machines.value?.length || 0;
});

const machinesSortOptions = computed(() => [
  { value: 'created_at', label: t('machines.sort.latest'), icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' },
  { value: 'year_desc', label: t('machines.sort.newestFirst'), icon: 'M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4' },
  { value: 'year_asc', label: t('machines.sort.oldestFirst'), icon: 'M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4' },
  { value: 'title', label: t('machines.sort.nameAZ'), icon: 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12' }
]);

const currentSortLabel = computed(() => {
  const option = machinesSortOptions.value.find(opt => opt.value === sortBy.value);
  return option ? option.label : t('machines.sort.latest');
});

const paginatedMachines = computed(() => {
  if (pagination.value && pagination.value.data) {
    return pagination.value.data;
  }
  if (pagination.value && pagination.value.total) {
    return machines.value;
  }
  const start = (currentPage.value - 1) * perPage.value;
  const end = start + perPage.value;
  return machines.value.slice(start, end);
});

const currentYear = computed(() => new Date().getFullYear());

const toggleFilters = () => {
  filtersVisible.value = !filtersVisible.value;
};

const selectSort = (value) => {
  sortBy.value = value;
  sortDropdownOpen.value = false;
  handleSortChange();
};

// Close dropdown when clicking outside
const handleClickOutside = (e) => {
  const dropdowns = document.querySelectorAll('.catalog-sort-dropdown');
  let clickedInside = false;
  dropdowns.forEach(dropdown => {
    if (dropdown.contains(e.target)) {
      clickedInside = true;
    }
  });
  if (!clickedInside) {
    sortDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const clearSearch = () => {
  searchQuery.value = '';
  debouncedSearch();
};

const debouncedSearch = useDebounceFn(() => {
  searchMachines();
}, 500);

const searchMachines = async () => {
  loading.value = true;

  try {
    const params = {
      ...filters.value,
      search: searchQuery.value,
      sort_by: sortBy.value,
      per_page: perPage.value,
      page: currentPage.value,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === null) {
        delete params[key];
      }
    });

    if (params.status && params.status !== "") {
      params.machine_status = params.status;
      delete params.status;
    }

    if (params.brand_id && params.brand_id !== "") {
      params.machine_brand = params.brand_id;
    }

    if (params.category_id && params.category_id !== "") {
      params.machine_category = params.category_id;
    }

    if (params.location && params.location !== "") {
      params.machine_location = params.location;
      delete params.location;
    }

    if (params.colors && params.colors !== "") {
      let colorArray = Array.isArray(params.colors) ? params.colors : [params.colors];
      colorArray = colorArray.filter(color => color && typeof color === 'string' && color.trim() !== '');
      if (colorArray.length > 0) {
        params.machine_color = colorArray;
      }
      delete params.colors;
    }

    if (params.condition && params.condition !== "") {
      params.machine_condition = params.condition;
      delete params.condition;
    }

    if (params.min_year && params.min_year !== "") {
      const minYear = parseInt(params.min_year);
      if (minYear >= 1980 && minYear <= currentYear.value) {
        params.machine_year_min = minYear;
      }
      delete params.min_year;
    }

    if (params.max_year && params.max_year !== "") {
      const maxYear = parseInt(params.max_year);
      if (maxYear >= 1980 && maxYear <= currentYear.value) {
        params.machine_year_max = maxYear;
      }
      delete params.max_year;
    }

    if (params.machine_year_min && params.machine_year_max) {
      if (params.machine_year_min > params.machine_year_max) {
        const temp = params.machine_year_min;
        params.machine_year_min = params.machine_year_max;
        params.machine_year_max = temp;
      }
    }

    // Always call backend index endpoint for filtering. The server-side
    // QueryFilter pipeline will handle empty/absent filters.
    const endpoint = "/machines";
    const requestParams = {
      ...params,
      sort_by: sortBy.value,
      per_page: perPage.value,
      page: currentPage.value,
    };
    // plugin expects options like { params: { ... } } or { query: { ... } }
    const response = await $api.get(endpoint, { params: requestParams });

    if (response.success && response.data) {
      machines.value = response.data;
      if (response.meta) {
        pagination.value = {
          current_page: response.meta.current_page,
          last_page: response.meta.last_page,
          per_page: response.meta.per_page,
          total: response.meta.total,
          from: response.meta.from,
          to: response.meta.to,
        };
        totalMachines.value = response.meta.total;
      } else {
        pagination.value = null;
        totalMachines.value = response.data.length;
      }
    } else if (response.data) {
      if (Array.isArray(response.data)) {
        machines.value = response.data;
        totalMachines.value = response.data.length;
        pagination.value = null;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        machines.value = response.data.data;
        totalMachines.value = response.data.pagination?.total || response.data.data.length;
        pagination.value = response.data.pagination;
      } else {
        machines.value = response.data || [];
        totalMachines.value = machines.value.length;
        pagination.value = null;
      }
    } else {
      machines.value = [];
      totalMachines.value = 0;
      pagination.value = null;
    }
  } catch (error) {
    machines.value = [];
    totalMachines.value = 0;
    pagination.value = null;
  } finally {
    loading.value = false;
  }
};

const clearFilters = () => {
  filters.value = {
    brand_id: "",
    category_id: "",
    colors: "",
    condition: "",
    location: "",
    min_year: "",
    max_year: "",
    status: "",  // Empty to let backend handle default (published + sold)
  };
  searchQuery.value = "";
  currentPage.value = 1;
  searchMachines();
};

const handlePageChange = (page) => {
  currentPage.value = page;
  searchMachines();
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
};

const handlePerPageChange = (value) => {
  perPage.value = value;
  currentPage.value = 1;
  searchMachines();
};

const handleSortChange = () => {
  currentPage.value = 1;
  searchMachines();
};

const fetchFilters = async () => {
  try {
    const response = await $api.get("/machines/filters");

    let filtersData;
    if (response.data && response.data.data) {
      filtersData = response.data.data;
    } else if (response.data) {
      filtersData = response.data;
    } else {
      filtersData = response;
    }

    if (filtersData.brands) {
      if (filtersData.brands.data && Array.isArray(filtersData.brands.data)) {
        filtersData.brands = filtersData.brands.data.filter(brand => brand && brand.id && brand.name);
      } else if (Array.isArray(filtersData.brands)) {
        filtersData.brands = filtersData.brands.filter(brand => brand && brand.id && brand.name);
      } else {
        filtersData.brands = [];
      }
    }

    if (filtersData.categories) {
      if (filtersData.categories.data && Array.isArray(filtersData.categories.data)) {
        filtersData.categories = filtersData.categories.data.filter(category => category && category.id && category.name);
      } else if (Array.isArray(filtersData.categories)) {
        filtersData.categories = filtersData.categories.filter(category => category && category.id && category.name);
      } else {
        filtersData.categories = [];
      }
    }

    if (filtersData.locations) {
      if (Array.isArray(filtersData.locations)) {
        filtersData.locations = filtersData.locations.filter(location => location);
      } else {
        filtersData.locations = [];
      }
    }

    if (!Array.isArray(filtersData.brands)) filtersData.brands = [];
    if (!Array.isArray(filtersData.categories)) filtersData.categories = [];
    if (!Array.isArray(filtersData.locations)) filtersData.locations = [];

    availableFilters.value = filtersData;
  } catch (error) {
    availableFilters.value = { brands: [], categories: [], printing_colors: {}, locations: [] };
  }
};

const applyRouteFilters = async () => {
  const route = useRoute();

  const resetFilters = () => {
    filters.value = {
      brand_id: "",
      category_id: "",
      colors: "",
      condition: "",
      location: "",
      min_year: "",
      max_year: "",
      status: "",  // Empty to let backend handle default (published + sold)
    };
  };

  resetFilters();

  if (route.query.category) filters.value.category_id = route.query.category.toString();
  if (route.query.brand) filters.value.brand_id = route.query.brand.toString();
  if (route.query.condition) filters.value.condition = route.query.condition.toString();
  if (route.query.year) {
    const year = parseInt(route.query.year.toString(), 10);
    if (!isNaN(year) && year >= 1980 && year <= currentYear.value) {
      filters.value.min_year = year;
      filters.value.max_year = year;
    }
  }
  if (route.query.type) filters.value.status = route.query.type.toString();

  watch(
    () => route.query,
    async () => {
      resetFilters();
      if (route.query.category) filters.value.category_id = route.query.category.toString();
      if (route.query.brand) filters.value.brand_id = route.query.brand.toString();
      if (route.query.condition) filters.value.condition = route.query.condition.toString();
      if (route.query.year) {
        const year = parseInt(route.query.year.toString(), 10);
        if (!isNaN(year) && year >= 1980 && year <= currentYear.value) {
          filters.value.min_year = year;
          filters.value.max_year = year;
        }
      }
      if (route.query.type) filters.value.status = route.query.type.toString();
      currentPage.value = 1;
      await searchMachines();
    }
  );
};

watch(sortBy, () => {
  currentPage.value = 1;
  searchMachines();
});

onMounted(async () => {
  const eventName = 'machines_page_view';
  useTrackEvent(eventName, {
    page_location: window.location.href,
  });
  const gtagConfig = useRuntimeConfig().public.gtag;
  if (import.meta.dev && gtagConfig.enabled && gtagConfig.id) {
    console.info('[gtag] event triggered', eventName);
  }

  await applyRouteFilters();
  await searchMachines();
  await fetchFilters();
});
</script>

<style scoped>
/* ============================================
   CATALOG PAGE - Modern Industrial Design
   ============================================ */

.catalog-page {
  min-height: 100vh;
  background: #f1f5f9;
  font-family: 'Open Sans', system-ui, sans-serif;
}

/* ============================================
   HEADER - Bold with Background
   ============================================ */

.catalog-header {
  position: relative;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  padding: 40px 0 48px;
  overflow: hidden;
}

.catalog-header__bg {
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(147, 197, 253, 0.1) 0%, transparent 40%);
  pointer-events: none;
}

.catalog-header__container {
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  z-index: 1;
}

.catalog-header__breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 24px;
}

.catalog-header__breadcrumb-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.catalog-header__breadcrumb-link:hover {
  color: #ffffff;
}

.catalog-header__breadcrumb-sep {
  color: rgba(255, 255, 255, 0.3);
  display: flex;
}

.catalog-header__breadcrumb-current {
  color: #ffffff;
  font-weight: 600;
}

.catalog-header__row {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (min-width: 900px) {
  .catalog-header__row {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
  }
}

.catalog-header__left {
  flex: 1;
}

.catalog-header__title {
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

@media (min-width: 768px) {
  .catalog-header__title {
    font-size: 42px;
  }
}

.catalog-header__subtitle {
  margin: 8px 0 0 0;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.65);
  max-width: 400px;
  line-height: 1.4;
}

.catalog-header__stats {
  display: inline-flex;
  margin-top: 16px;
}

.catalog-header__stat {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50px;
}

.catalog-header__stat-number {
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1;
}

.catalog-header__stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* Search in Header - Right Side */
.catalog-header__search {
  flex-shrink: 0;
  width: 100%;
}

@media (min-width: 900px) {
  .catalog-header__search {
    width: 380px;
  }
}

/* ============================================
   FILTERS SECTION - Modern Pills Style
   ============================================ */

.catalog-filters {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 40;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.catalog-filters__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 24px;
}

/* Search Bar - Prominent Style */
.catalog-search {
  position: relative;
  display: flex;
  align-items: center;
}

.catalog-search__icon {
  position: absolute;
  left: 18px;
  width: 22px;
  height: 22px;
  color: #ffffff;
  pointer-events: none;
  z-index: 2;
}

.catalog-search__input {
  width: 100%;
  padding: 16px 50px 16px 52px;
  font-size: 15px;
  font-weight: 500;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  color: #ffffff;
  transition: all 0.2s ease;
}

.catalog-search__input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.1);
}

.catalog-search__input::placeholder {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
}

.catalog-search__clear {
  position: absolute;
  right: 14px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.catalog-search__clear:hover {
  background: rgba(255, 255, 255, 0.3);
}

.catalog-search__clear svg {
  width: 16px;
  height: 16px;
  color: #ffffff;
}

/* Mobile Filter Toggle */
.catalog-filters__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;
}

.catalog-filters__toggle:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-color: #cbd5e1;
}

.catalog-filters__toggle svg {
  width: 20px;
  height: 20px;
  color: #64748b;
}

.catalog-filters__badge {
  background: #0f172a;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
}

/* Hide mobile toggle on desktop, show row */
@media (min-width: 769px) {
  .catalog-filters__toggle {
    display: none;
  }
  
  .catalog-filters__row {
    display: flex;
  }
}

/* Hide row on mobile, show toggle */
@media (max-width: 768px) {
  .catalog-filters__row {
    display: none;
  }
  
  .catalog-filters__toggle {
    display: flex;
  }
}

/* Filter Controls */
.catalog-filters__controls {
  display: none;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.catalog-filters__controls--visible {
  display: block;
}

@media (min-width: 769px) {
  .catalog-filters__controls {
    display: none !important;
  }
}

.catalog-filters__row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  padding-bottom: 4px;
}

.catalog-filters__grid {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .catalog-filters__grid {
    display: none;
  }
  
  .catalog-filters__controls .catalog-filters__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    width: 100%;
  }
  
  .catalog-filters__controls .catalog-filter {
    width: 100%;
  }
  
  .catalog-filters__controls .catalog-filter__select {
    width: 100%;
  }
  
  .catalog-filters__controls .catalog-filter--year {
    grid-column: 1 / -1;
  }
}

/* Individual Filter - Compact Pill */
.catalog-filter {
  flex-shrink: 0;
}

.catalog-filter__label {
  display: none;
}

.catalog-filter__select {
  padding: 10px 36px 10px 14px;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid #cbd5e1;
  border-radius: 50px;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 14px;
  white-space: nowrap;
}

.catalog-filter__select:hover {
  border-color: #3b82f6;
  background-color: #f8fafc;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.catalog-filter__select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

/* Year Range - Inline */
.catalog-filter--year {
  flex-shrink: 0;
}

.catalog-filter__year-range {
  display: flex;
  align-items: center;
  border: 2px solid #cbd5e1;
  border-radius: 50px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.catalog-filter__year-range:hover {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.catalog-filter__year-range:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.catalog-filter__year-input {
  width: 75px;
  padding: 10px 6px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  border: none;
  background: transparent;
  color: #0f172a;
}

.catalog-filter__year-input:focus {
  outline: none;
  background: #f8fafc;
}

.catalog-filter__year-input::placeholder {
  color: #94a3b8;
  font-weight: 500;
}

.catalog-filter__year-separator {
  color: #cbd5e1;
  font-size: 12px;
  padding: 0 4px;
}

/* Mobile Year Range - Full Width */
@media (max-width: 768px) {
  .catalog-filters__controls .catalog-filter__year-range {
    width: 100%;
    justify-content: center;
  }
  
  .catalog-filters__controls .catalog-filter__year-input {
    flex: 1;
    max-width: 120px;
  }
}

/* Filter Actions - Inline */
.catalog-filters__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;
}

@media (max-width: 768px) {
  .catalog-filters__actions {
    display: none;
  }
  
  .catalog-filters__controls .catalog-filters__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    width: 100%;
    margin-left: 0;
    margin-top: 16px;
  }
  
  .catalog-filters__controls .catalog-sort-dropdown {
    width: 100%;
  }
  
  .catalog-filters__controls .catalog-sort-dropdown__trigger {
    width: 100%;
    justify-content: center;
  }
  
  .catalog-filters__controls .catalog-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Sort Dropdown - Modern Design */
.catalog-sort-dropdown {
  position: relative;
  z-index: 50;
}

.catalog-sort-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: #334155;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.catalog-sort-dropdown__trigger:hover {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #ffffff 0%, #eff6ff 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-1px);
}

.catalog-sort-dropdown.is-open .catalog-sort-dropdown__trigger {
  border-color: #3b82f6;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.catalog-sort-dropdown__icon {
  width: 16px;
  height: 16px;
  color: #3b82f6;
  flex-shrink: 0;
}

.catalog-sort-dropdown__label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
}

.catalog-sort-dropdown__chevron {
  width: 14px;
  height: 14px;
  color: #94a3b8;
  transition: transform 0.25s ease;
  flex-shrink: 0;
}

.catalog-sort-dropdown.is-open .catalog-sort-dropdown__chevron {
  transform: rotate(180deg);
  color: #3b82f6;
}

.catalog-sort-dropdown__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 8px;
  animation: dropdownFadeIn 0.2s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.catalog-sort-dropdown__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.catalog-sort-dropdown__item:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.catalog-sort-dropdown__item.is-active {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1d4ed8;
}

.catalog-sort-dropdown__item-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #64748b;
}

.catalog-sort-dropdown__item-label {
  flex: 1;
}

.catalog-sort-dropdown__check {
  width: 16px;
  height: 16px;
  color: #3b82f6;
}

/* Buttons */
.catalog-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 50px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}

.catalog-btn svg {
  width: 14px;
  height: 14px;
}

.catalog-btn--primary {
  background: #0f172a;
  color: #ffffff;
}

.catalog-btn--primary:hover {
  background: #1e293b;
}

.catalog-btn--danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.catalog-btn--danger:hover {
  background: #fee2e2;
}

/* ============================================
   RESULTS BAR - Subtle
   ============================================ */

.catalog-results-bar {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 0;
}

.catalog-results-bar__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

@media (min-width: 640px) {
  .catalog-results-bar__container {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.catalog-results-bar__info {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.catalog-results-bar__count {
  font-weight: 700;
  color: #0f172a;
}

.catalog-results-bar__pagination {
  color: #94a3b8;
}

.catalog-results-bar__per-page {
  display: flex;
  align-items: center;
  gap: 10px;
}

.catalog-results-bar__per-page-label {
  font-size: 13px;
  color: #64748b;
}

.catalog-results-bar__per-page-buttons {
  display: flex;
  gap: 4px;
}

.catalog-results-bar__per-page-btn {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.catalog-results-bar__per-page-btn:hover {
  border-color: #cbd5e1;
  color: #374151;
}

.catalog-results-bar__per-page-btn--active {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

/* ============================================
   MAIN CONTENT
   ============================================ */

.catalog-main {
  padding: 32px 0 64px;
}

.catalog-main__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Product Grid */
.catalog-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.catalog-grid > * {
  width: 100%;
}

/* Loading Skeleton */
.catalog-loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.catalog-skeleton {
  display: flex;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  height: 160px;
}

@media (max-width: 768px) {
  .catalog-skeleton {
    flex-direction: column;
    height: auto;
  }
}

.catalog-skeleton__image {
  width: 280px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (max-width: 768px) {
  .catalog-skeleton__image {
    width: 100%;
    height: 180px;
  }
}

.catalog-skeleton__content {
  flex: 1;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
}

@media (max-width: 768px) {
  .catalog-skeleton__content {
    flex-direction: column;
    align-items: stretch;
  }
}

.catalog-skeleton__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.catalog-skeleton__title {
  height: 20px;
  width: 60%;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.catalog-skeleton__subtitle {
  height: 16px;
  width: 30%;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.catalog-skeleton__specs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-top: 6px;
}

.catalog-skeleton__spec {
  height: 16px;
  width: 80px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.catalog-skeleton__status {
  width: 80px;
  height: 26px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 20px;
  flex-shrink: 0;
}

.catalog-skeleton__actions {
  flex-shrink: 0;
}

.catalog-skeleton__btn {
  height: 40px;
  width: 140px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .catalog-skeleton__btn {
    width: 100%;
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Empty State */
.catalog-empty {
  text-align: center;
  padding: 64px 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.catalog-empty__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.catalog-empty__icon svg {
  width: 32px;
  height: 32px;
  color: #94a3b8;
}

.catalog-empty__title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.catalog-empty__text {
  margin: 0 0 24px 0;
  font-size: 15px;
  color: #64748b;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
}

/* Pagination */
.catalog-pagination {
  margin-top: 40px;
}
</style>
